// Notifications: TanStack Query hooks + type definitions (TMF-464).
//
// The durable per-user inbox behind the Home panel: one row per notice, per
// recipient. Rows are written server-side by the consumer that announces an
// overdue task, so there is no create hook here and no endpoint behind one.
// The one write a client makes is marking a notice read.
//
// The API scopes every read to the authenticated user, so nothing here sends a
// user filter: a client cannot ask for somebody else's inbox. Mirrors
// lib/tasks.ts for its resource.

import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

import { reportActionError } from "~/lib/actionErrors";
import { halFetch, type HalCollection, type HalResource } from "~/lib/hal";

/**
 * What a notice is about. Closed, mirroring enums.NotificationType, and the
 * value IS the display string.
 */
export type NotificationType = "Task overdue";

/**
 * One notice in the current user's inbox. `body` is the sentence the firm
 * reads, composed when the notice was written, and `read_at` is null until the
 * recipient opens it.
 */
export type Notification = HalResource<{
  id: string;
  user_id: string;
  project_id: string;
  task_id: string;
  type: NotificationType;
  body: string;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}>;

export type NotificationCollection = HalCollection<Notification>;

/** Invalidation root for every notifications cache entry. The realtime
 *  channel invalidates this key when a user-scoped notifications signal
 *  arrives (lib/realtime/clientKeyFor.ts). */
export const NOTIFICATIONS_KEY = ["notifications"] as const;

/** One page of the inbox, newest first (the API's default order). Twenty is
 *  what the Home panel shows: it is a glance, not an archive. */
export const notificationsListQueryOptions = () =>
  queryOptions<NotificationCollection>({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ size: "20" });
      return halFetch<NotificationCollection>(`/api/notifications?${params.toString()}`, { signal });
    },
    staleTime: 30 * 1000,
  });

/**
 * Mark one notice read. The client states the fact about the resource
 * (`read: true`) and the API stamps the moment; a second call is a no-op
 * server-side, so a double click costs nothing.
 */
export function markNotificationRead(id: string): Promise<Notification> {
  return halFetch<Notification>(`/api/notifications/${id}`, {
    method: "PUT",
    body: { read: true },
  });
}

/** markNotificationRead as a mutation, invalidating the inbox the unread count
 *  is derived from. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
    onError: (error) => reportActionError(error),
  });
}

/** How many of the loaded notices are unread, which is what the panel's badge
 *  shows. */
export function unreadCount(notifications: Notification[]): number {
  return notifications.filter((notification) => notification.read_at === null).length;
}
